/**
 * GstPage — /legal/gst
 *
 * Canonical GST stance for the HustleSuite ecosystem. Explains
 * inclusive consumer pricing, exclusive vendor pricing, and the
 * paperless transaction-history model.
 */
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Receipt, Archive, Search, Printer, Users, Store } from "lucide-react";
import { Link } from "react-router-dom";

export default function GstPage() {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="gst-page">
      <PlatformHeader brand={{ name: "HustleSuite", to: "/" }} showBookCta={false} showCart={false} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-6">
          <Receipt className="text-amber-500" size={26}/>
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            GST stance · effective 9 Feb 2026
          </p>
        </div>
        <h1 className="text-4xl md:text-5xl font-black mb-4 text-stone-900">
          GST across HustleSuite
        </h1>
        <p className="text-lg text-stone-600 mb-10 leading-relaxed">
          A plain-English summary of how the 10% Australian Goods and
          Services Tax is displayed, collected, and remitted across every
          part of the HustleSuite ecosystem.
        </p>

        <div className="prose prose-stone max-w-none prose-headings:font-black prose-headings:text-stone-900 prose-p:text-stone-700 prose-li:text-stone-700 prose-a:text-amber-600">

          {/* Two-sided pricing */}
          <h2>1. Two-sided pricing — inclusive for members, exclusive for vendors</h2>
          <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5">
              <Users className="text-emerald-600 mb-2" size={22}/>
              <div className="font-black text-emerald-900 mb-1">Members &amp; consumers</div>
              <p className="text-sm text-emerald-900/90 mb-0">
                All prices displayed across the HustleSuite are shown{" "}
                <strong>including 10% Australian GST</strong>. No surprises at
                checkout — the number you see is the number you pay.
              </p>
            </div>
            <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-5">
              <Store className="text-slate-700 mb-2" size={22}/>
              <div className="font-black text-slate-900 mb-1">Vendors &amp; partners</div>
              <p className="text-sm text-slate-800 mb-0">
                All vendor-facing pricing, payouts, commission tables and
                rate cards are quoted <strong>exclusive of GST (ex-GST)</strong>.
                The GST component is calculated and remitted separately via the
                Stripe Connect payout ledger.
              </p>
            </div>
          </div>

          <h2>2. We do not email or print GST invoices</h2>
          <p>
            It is not our practice to email or print GST invoices with each
            transaction. Every transaction is automatically added to your
            member area under <strong>Transactions</strong>. From there you can:
          </p>
          <ul>
            <li>Print any receipt for a paper copy.</li>
            <li>Email the receipt directly to your bookkeeper or accountant.</li>
            <li>Download a PDF copy for your own records.</li>
            <li>Bulk-export a date range for BAS/tax time.</li>
          </ul>
          <p>
            This paperless-by-default approach cuts inbox clutter and lines up
            with the ATO's electronic-record-keeping guidance for retail and
            hospitality merchants.
          </p>

          {/* Feature cards */}
          <div className="not-prose grid md:grid-cols-3 gap-3 my-8">
            {[
              [Archive, "Yearly archives",
               "Older transactions are moved to yearly archives so your active view stays fast."],
              [Search, "Search anything",
               "Filter by vendor name, date, amount, item description, or invoice number."],
              [Printer, "Retrieve on demand",
               "Any archived transaction can be retrieved and re-printed if required."],
            ].map(([I, title, body], i) => (
              <div key={i} className="rounded-2xl border border-stone-200 bg-white p-5">
                <I size={20} className="text-amber-500 mb-2"/>
                <div className="font-black text-stone-900 mb-1">{title}</div>
                <div className="text-sm text-stone-600">{body}</div>
              </div>
            ))}
          </div>

          <h2>3. Your transaction history</h2>
          <p>
            Every purchase, top-up, refund, wallet debit, or Hustle Points
            redemption made under your member account is stored in your{" "}
            <Link to="/account/transactions" className="underline">Transactions</Link>{" "}
            area. Records include:
          </p>
          <ul>
            <li><strong>Vendor name</strong> — the participating Hustle Vendor or module.</li>
            <li><strong>Date &amp; time</strong> — the AEST/AEDT timestamp of the transaction.</li>
            <li><strong>Amount</strong> — the GST-inclusive total in AUD.</li>
            <li><strong>Item description</strong> — line-item breakdown where the vendor provided it.</li>
            <li><strong>Invoice number</strong> — our internal reference and (if the vendor issued one) their invoice number.</li>
            <li><strong>Payment method</strong> — card, wallet, or Hustle Points.</li>
          </ul>

          <h2>4. Archives &amp; retrieval</h2>
          <p>
            Transaction records are archived <strong>by year</strong>. Older years
            stay searchable via the archive filter, and any single transaction
            can be retrieved and printed on demand — even years later — for
            audit, warranty, or reimbursement claims.
          </p>

          <h2>5. Vendor payout GST</h2>
          <p>
            Where you sell through the HustleSuite as a registered vendor,
            your Stripe Connect payout ledger separates the GST component of
            each sale so you can complete your BAS with a single export.
            HustleSuite does not withhold GST — you remit it to the ATO under
            your own ABN.
          </p>

          <h2>6. Corrections &amp; queries</h2>
          <p>
            Spotted a transaction that looks wrong? Open the record in your
            Transactions area and use the <strong>Query this transaction</strong>{" "}
            action, or email <a href="mailto:accounts@hustlesuite.au">accounts@hustlesuite.au</a>{" "}
            quoting the invoice number. Corrections are usually resolved
            within one business day.
          </p>

          <hr className="my-8"/>
          <p className="text-xs text-stone-500">
            This is a plain-English summary. Nothing on this page constitutes
            tax advice — talk to a registered tax agent about your own
            circumstances. Corporations Act, ATO rulings, and the Australian
            Consumer Law prevail over anything summarised here.
          </p>
        </div>
      </div>
      <SuiteFooter/>
    </div>
  );
}
